import { type IFetchOrders } from '@/types/IFetchOrders';
import { type IFetchOrderParams } from '@/types/IFetchOrderParams';
import { type IFiltersForDownloadRoute } from '@/types/IFiltersForDownloadRoute';
import {
  ORDER_LIST_MAX_LIMIT,
  ORDERS_ROUTE_SHEET_PATH
} from '@/constants/ordersList';
import { createOrdersUrl } from '../utils/createOrdersUrl';
import {
  CustomError,
  getErrorData,
  type IErrorData
} from '../utils/getErrorData';
import axios, { type AxiosRequestConfig } from 'axios';
import { getHeaders } from '../utils/getHeaders';
import { api } from '../utils/api';

export interface OrdersByParamsOptionsType {
  filters: IFetchOrderParams;
  signal: AbortSignal;
}

export interface RouteSheetOptionsType {
  filters: IFiltersForDownloadRoute;
  signal: AbortSignal;
}

export type AbortErrorType = 'Abort';

/**
 * `GET /api/admin/v2/orders` — список заказов (PAR-5189).
 *
 * Элементы выдачи — `IOrderListItem`: без позиций, способа оплаты и
 * комментария клиента. `limit` прижимается к потолку контракта: большее
 * значение из адресной строки стенд отбивает HTML-страницей вместо JSON.
 */
export const getOrdersByParams = async (
  props: OrdersByParamsOptionsType
): Promise<IFetchOrders | IErrorData | AbortErrorType> => {
  const { filters, signal } = props;
  try {
    if (filters === null || filters === undefined) {
      throw new Error('Отсутствуют параметры поиска для запроса');
    }

    const headers = getHeaders();
    const url = createOrdersUrl({
      ...filters,
      limit:
        filters.limit !== undefined
          ? Math.min(filters.limit, ORDER_LIST_MAX_LIMIT)
          : undefined
    });
    const requestOptions: AxiosRequestConfig = {
      method: 'GET',
      headers,
      signal
    };

    const response = await api.get<IFetchOrders>(url, requestOptions);

    if (response.status === 500) {
      throw new CustomError(
        'При получении заказов произошла необработанная ошибка'
      );
    }

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw new Error('Запрос отменен');
    }
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error('Ошибка при получении заказов:', error);
    return errorData;
  }
};

/**
 * `GET /api/admin/orders/download` — ссылка на PDF маршрутного листа.
 *
 * ⚠️ На `par-5087` 2026-09-15 ручка отвечает `500` на любой запрос, даже
 * пустой (блокер бэкенда).
 */
export const downloadRouteSheet = async (
  props: RouteSheetOptionsType
): Promise<string | IErrorData> => {
  const { filters, signal } = props;
  const url = createOrdersUrl(filters, ORDERS_ROUTE_SHEET_PATH);
  const headers = getHeaders();
  const requestOptions: AxiosRequestConfig = {
    method: 'GET',
    headers,
    signal
  };

  try {
    const response = await api.get<{ response: string }>(url, requestOptions);

    if (response.status === 500) {
      throw new CustomError(
        'При скачивании маршрутного листа произошла необработанная ошибка'
      );
    }

    const fileUrl = response.data.response;
    return fileUrl;
  } catch (error) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error('Ошибка при скачивании маршрутного листа:', error);
    return errorData;
  }
};
